/**
 * Helpers partagés côté serveur + client pour la normalisation et l'affichage
 * des URLs de site web des prospects.
 *
 * Convention produit (validée 2026-04-24) :
 *   - À la saisie, l'utilisateur peut taper "www.adelie-assurances.com" sans
 *     préfixe : on ajoute `https://` à l'enregistrement.
 *   - Si l'utilisateur a explicitement tapé `http://`, on RESPECTE sa saisie
 *     (use case : intranet ou site legacy). Option A validée.
 *   - À l'affichage, on retire le protocole (et trailing slash) pour alléger
 *     la lecture : "www.adelie-assurances.com". La valeur stockée conserve
 *     le protocole pour être utilisable en `href`.
 */

/**
 * Normalise une valeur de site web avant persistance.
 *
 * Cas :
 *   - `null` / `undefined` / chaîne vide / whitespace-only → `null`
 *   - Commence par `http://` ou `https://` (casse insensible) → trim + laissé
 *     tel quel (respect de l'intention explicite de l'utilisateur).
 *   - Sinon → préfixé `https://` + trim.
 *
 * La validation stricte (URL bien formée) est faite en aval par Zod `.url()`.
 */
export function normalizeWebsite(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Retourne la forme compacte pour l'affichage : protocole retiré + trailing
 * slash retiré. `null` / `undefined` / vide → chaîne vide.
 *
 * Exemples :
 *   - "https://www.adelie-assurances.com"  → "www.adelie-assurances.com"
 *   - "http://intranet.local/"             → "intranet.local"
 *   - "https://example.com/contact"        → "example.com/contact"
 *   - null                                 → ""
 */
export function displayWebsite(url: string | null | undefined): string {
  if (!url) return '';
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}
